export type PlanId = "standard" | "pro" | "unlimited";
export type CheckoutOptionId = "standard" | "pro" | "standard_trial" | "pro_trial" | "pro_lifetime";
export type CheckoutBillingMode = "annual_trial" | "one_time";
export type CheckoutPlan = {
  id: CheckoutOptionId;
  planId: Exclude<PlanId, "unlimited">;
  offer: "lifetime" | "polar_trial_7d" | "site_lifetime";
  billingMode: CheckoutBillingMode;
  name: string;
  label: string;
  originalPrice?: string;
  price: string;
  priceSuffix?: string;
  detail: string;
  description: string;
  cta: string;
  badge?: string;
  badgeVariant?: "primary";
  features: string[];
};

export type QuestionOptionTone = "blue" | "green" | "amber" | "violet";

export type Question = {
  id: string;
  /** Legacy field; the step label is now computed as "Step N of M" in App. Optional. */
  eyebrow?: string;
  title: string;
  subtitle: string;
  /** Multi-select questions store their answer as a comma-joined, sorted id list. */
  multiSelect?: boolean;
  /** Compact tiles: logo + label only, no descriptions (for options people already know). */
  compact?: boolean;
  options: Array<{
    id: string;
    label: string;
    description: string;
    icon: "moon" | "battery" | "terminal" | "clock" | "shield" | "desktop" | "coin";
    /** Real product logo, when we have one — takes precedence over `icon`. */
    brand?: "claude" | "openai" | "cursor" | "gemini" | "opencode" | "openclaw" | "hermes";
    tone: QuestionOptionTone;
  }>;
};

export type Answers = Record<string, string>;

export const questions: Question[] = [
  {
    id: "agents_used",
    eyebrow: "Step 1 of 4",
    title: "Which coding agents do you run?",
    subtitle: "Pick all that apply. AgentMax puts every one of them on the same notch.",
    multiSelect: true,
    compact: true,
    options: [
      {
        id: "claude_code",
        label: "Claude Code",
        description: "Long runs in the terminal: refactors, tests, big tasks.",
        icon: "terminal",
        brand: "claude",
        tone: "violet",
      },
      {
        id: "codex",
        label: "Codex CLI",
        description: "OpenAI's agent grinding through jobs in your terminal.",
        icon: "desktop",
        brand: "openai",
        tone: "blue",
      },
      {
        id: "cursor",
        label: "Cursor",
        description: "Agent mode running inside the editor.",
        icon: "moon",
        brand: "cursor",
        tone: "green",
      },
      {
        id: "gemini",
        label: "Gemini CLI",
        description: "Google's agent working through your terminal.",
        icon: "terminal",
        brand: "gemini",
        tone: "blue",
      },
      {
        id: "opencode",
        label: "OpenCode",
        description: "Open-source agent runs, local-first.",
        icon: "terminal",
        brand: "opencode",
        tone: "amber",
      },
      {
        id: "other",
        label: "Something else",
        description: "Aider, Windsurf, custom scripts, it still shows up.",
        icon: "terminal",
        tone: "violet",
      },
    ],
  },
  {
    id: "agent_hours",
    eyebrow: "Step 2 of 4",
    title: "How long do they run on a typical day?",
    subtitle: "Count all of it, especially the runs you walk away from.",
    options: [
      {
        id: "under_1h",
        label: "Under an hour",
        description: "Quick tasks. You mostly watch them finish.",
        icon: "clock",
        tone: "blue",
      },
      {
        id: "one_to_three",
        label: "1 to 3 hours",
        description: "Agents work while you do other things.",
        icon: "moon",
        tone: "green",
      },
      {
        id: "three_to_six",
        label: "3 to 6 hours",
        description: "Multiple agents going, often at the same time.",
        icon: "terminal",
        tone: "violet",
      },
      {
        id: "six_plus",
        label: "6+ hours / overnight",
        description: "The close-the-lid, wake-up-to-results kind of runs.",
        icon: "battery",
        tone: "amber",
      },
    ],
  },
  {
    id: "agent_pain",
    eyebrow: "Step 3 of 4",
    title: "What's the most annoying part of running them?",
    subtitle: "The one you'd fix first.",
    options: [
      {
        id: "waiting_unseen",
        label: "Finding & checking each one",
        description: "Cmd-tab roulette across terminals to see who's done, stuck, or waiting on you.",
        icon: "desktop",
        tone: "blue",
      },
      {
        id: "unknown_costs",
        label: "Costs spread across providers",
        description: "Anthropic, OpenAI, Cursor, no single number for what today cost.",
        icon: "coin",
        tone: "violet",
      },
      {
        id: "rate_limits",
        label: "Plan limits hit without warning",
        description: "An agent dies mid-task because you couldn't see the cap coming.",
        icon: "clock",
        tone: "amber",
      },
      {
        id: "mac_sleeps",
        label: "Can't close the lid",
        description: "Long runs die the moment the lid closes or the Mac sleeps.",
        icon: "moon",
        tone: "green",
      },
    ],
  },
  {
    id: "persona_level",
    title: "What best describes you?",
    subtitle: "Helps us tune AgentMax to how you work.",
    options: [
      {
        id: "learning",
        label: "Learning to code",
        description: "Getting the hang of it. Agents help me move faster.",
        icon: "terminal",
        tone: "blue",
      },
      {
        id: "professional",
        label: "Professional developer",
        description: "I ship code for work, most days.",
        icon: "desktop",
        tone: "green",
      },
      {
        id: "senior",
        label: "Senior / staff engineer",
        description: "I run several agents and own big surfaces.",
        icon: "shield",
        tone: "violet",
      },
      {
        id: "founder",
        label: "Founder / solo builder",
        description: "Building my own thing, wearing every hat.",
        icon: "clock",
        tone: "amber",
      },
    ],
  },
  {
    id: "mac_setup",
    eyebrow: "Step 4 of 4",
    title: "Which setup sounds closest?",
    subtitle: "Last one. We'll match you to the right license.",
    options: [
      {
        id: "one_mac",
        label: "One main Mac",
        description: "Personal workflow, simple setup, one device.",
        icon: "desktop",
        tone: "blue",
      },
      {
        id: "multi_mac",
        label: "Up to five Macs",
        description: "Work machine, home machine, or a multi-device setup.",
        icon: "shield",
        tone: "green",
      },
      {
        id: "team_research",
        label: "Testing for a team",
        description: "Start with the best value license and expand later.",
        icon: "clock",
        tone: "violet",
      },
    ],
  },
];

// Concept-specific questions that REPLACE the shared "agent_pain" slot (step 4 of 5)
// for the fake-door / feature-led ads. Same position and count, so routing and step
// math are unchanged. Each qualifies demand for that ad's specific promise.
export const voiceDemandQuestion: Question = {
  id: "voice_demand",
  title: "When would talking to your agents help most?",
  subtitle: "Voice is faster than typing every prompt and approval.",
  options: [
    {
      id: "fire_off",
      label: "Firing off tasks fast",
      description: "Kick off a run by just saying it.",
      icon: "terminal",
      tone: "violet",
    },
    {
      id: "approve",
      label: "Approving without stopping",
      description: "Say yes to the one that's waiting.",
      icon: "desktop",
      tone: "blue",
    },
    {
      id: "hands_busy",
      label: "When my hands are busy",
      description: "Away from the keyboard or mid-task.",
      icon: "moon",
      tone: "green",
    },
    {
      id: "typing_slow",
      label: "All of it, typing slows me down",
      description: "I'd rather talk than type.",
      icon: "clock",
      tone: "amber",
    },
  ],
};

export const loopDemandQuestion: Question = {
  id: "loop_demand",
  title: "What would you run overnight if it just worked?",
  subtitle: "Set a task tonight, wake up to it done.",
  options: [
    {
      id: "big_job",
      label: "A big refactor or migration",
      description: "The long job I keep putting off.",
      icon: "clock",
      tone: "amber",
    },
    {
      id: "backlog",
      label: "My backlog of small tasks",
      description: "A pile I'd batch while I sleep.",
      icon: "terminal",
      tone: "violet",
    },
    {
      id: "ci_grind",
      label: "Tests, builds, and CI grinds",
      description: "Long jobs that tie up my machine.",
      icon: "desktop",
      tone: "blue",
    },
    {
      id: "unsure_try",
      label: "Not sure yet, but I'd try it",
      description: "Curious what I could offload.",
      icon: "moon",
      tone: "green",
    },
  ],
};

export const SWAP_QUESTIONS = {
  voice_demand: voiceDemandQuestion,
  loop_demand: loopDemandQuestion,
} as const;

export type QuizSwapId = keyof typeof SWAP_QUESTIONS;
export const SWAP_QUESTION_IDS: readonly string[] = Object.keys(SWAP_QUESTIONS);

const SWAPPED_SLOT_ID = "agent_pain";

/**
 * The active quiz for a concept. Everyone gets the shared 5-question quiz; concepts
 * that name a `quizSwapId` swap their demand question into the "agent_pain" slot,
 * keeping the same length and positions so step routing is untouched.
 */
export const getQuestionsForConcept = (
  quizSwapId?: QuizSwapId | null,
  /** Per-question subtitle overrides, keyed by question id, so the quiz carries the concept's angle. */
  subtitleOverrides?: Readonly<Record<string, string>>,
): Question[] => {
  const swap = quizSwapId ? SWAP_QUESTIONS[quizSwapId] : undefined;
  if (!swap && !subtitleOverrides) return questions;
  return questions.map((question) => {
    const base = swap && question.id === SWAPPED_SLOT_ID ? swap : question;
    const subtitle = subtitleOverrides?.[base.id];
    return subtitle ? { ...base, subtitle } : base;
  });
};

export const getRecommendedPlan = (answers: Answers): Exclude<PlanId, "unlimited"> =>
  answers.mac_setup === "one_mac" ? "standard" : "pro";

const findAnswerLabel = (questionId: string, optionId: string | undefined): string | null => {
  if (!optionId) return null;
  const question = questions.find((item) => item.id === questionId);
  return question?.options.find((option) => option.id === optionId)?.label ?? null;
};

// Honest, transparent estimate of weekly agent runtime you can't see into.
// Derived purely from the user's own answers. No randomness, no inflation.
const HOURS_PER_DAY: Record<string, number> = {
  under_1h: 0.75,
  one_to_three: 2,
  three_to_six: 4.5,
  six_plus: 8,
};

const AGENT_NAMES: Record<string, string> = {
  claude_code: "Claude Code",
  codex: "Codex CLI",
  cursor: "Cursor",
  gemini: "Gemini CLI",
  opencode: "OpenCode",
  openclaw: "OpenClaw",
  hermes: "Hermes",
  other: "Other agents",
};

const BRAND_BY_AGENT: Record<string, "claude" | "openai" | "cursor" | "gemini" | "opencode" | "openclaw" | "hermes"> = {
  claude_code: "claude",
  codex: "openai",
  cursor: "cursor",
  gemini: "gemini",
  opencode: "opencode",
  openclaw: "openclaw",
  hermes: "hermes",
};

/** Logo + name pairs for the picked agents (falls back to the big three). */
export const getPickedAgentChips = (
  csv: string | undefined,
): Array<{ brand: "claude" | "openai" | "cursor" | "gemini" | "opencode" | "openclaw" | "hermes"; label: string }> => {
  const chips = (csv ?? "")
    .split(",")
    .filter((id) => BRAND_BY_AGENT[id])
    .map((id) => ({ brand: BRAND_BY_AGENT[id], label: AGENT_NAMES[id] ?? id }));
  if (chips.length > 0) return chips;
  return [
    { brand: "claude", label: "Claude Code" },
    { brand: "openai", label: "Codex CLI" },
    { brand: "cursor", label: "Cursor" },
  ];
};

/** Human fleet description for a comma-joined agents_used answer. */
export const describeAgents = (csv: string | undefined): string => {
  const names = (csv ?? "")
    .split(",")
    .filter(Boolean)
    .map((id) => AGENT_NAMES[id] ?? id);
  if (names.length === 0) return "Your agents";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} + ${names[1]}`;
  return `${names.length}-agent fleet`;
};

/** Grammatical list of the picked agents, e.g. "Claude Code, Cursor and Codex" (falls back to the big three). */
export const listPickedAgents = (csv: string | undefined): string => {
  const names = getPickedAgentChips(csv).map((agent) => agent.label);
  const shown = names.slice(0, 3);
  const extra = names.length - shown.length;
  let list =
    shown.length <= 1
      ? shown[0] ?? "your agents"
      : `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
  if (extra > 0) list += `, plus ${extra} more`;
  return list;
};

// Short noun phrase for the metric sentence ("...running with untracked costs").
const RISK_SHORT: Record<string, string> = {
  rate_limits: "surprise plan limits",
  unknown_costs: "costs scattered across providers",
  waiting_unseen: "agents lost across terminals",
  mac_sleeps: "runs dying when the lid closes",
};

type IconName = Question["options"][number]["icon"];

// The feature that answers each annoyance — shown as the result step's key-feature block.
const KEY_FEATURE: Record<string, { title: string; copy: string; icon: IconName }> = {
  waiting_unseen: {
    title: "Agent Island macOS notch",
    copy: "See every agent live in your Mac's notch. Know what is working, waiting, or done, then jump straight back.",
    icon: "desktop",
  },
  unknown_costs: {
    title: "One cost number, every provider",
    copy: "Daily spend per agent, added up locally from the files Claude Code, Codex, and Cursor already write.",
    icon: "coin",
  },
  rate_limits: {
    title: "Limit forecast",
    copy: "AgentMax projects your plan usage and warns you before the cap: \u201climit ~6:00pm\u201d, not a dead agent mid-task.",
    icon: "clock",
  },
  mac_sleeps: {
    title: "Close-lid agent continuity",
    copy: "Your Mac stays awake while agents work. The display can sleep, the lid can close, and the battery stays guarded.",
    icon: "moon",
  },
};

export const resultFeatureOverview = [
  { title: "Agent Island macOS notch", icon: "desktop" },
  { title: "Close-lid agent continuity on Mac", icon: "moon" },
  { title: "One cost number, every provider", icon: "coin" },
  { title: "Traffic-light visibility into every run", icon: "terminal" },
  { title: "Limit forecasts before caps hit", icon: "clock" },
] satisfies ReadonlyArray<{ title: string; icon: IconName }>;

const RISK_ICON: Record<string, IconName> = {
  rate_limits: "clock",
  unknown_costs: "coin",
  waiting_unseen: "desktop",
  mac_sleeps: "moon",
};

export const getResultInsights = (answers: Answers) => {
  const sessionBreak = findAnswerLabel("agent_pain", answers.agent_pain) ?? "Running blind";
  const macSetup = findAnswerLabel("mac_setup", answers.mac_setup) ?? "Multi-Mac setup";
  const recommendedPlan = getRecommendedPlan(answers);
  const planName = recommendedPlan === "standard" ? "Standard" : "Pro";
  const planDetail = recommendedPlan === "standard" ? "1 Mac" : "up to 5 Macs";

  const hoursPerDay = HOURS_PER_DAY[answers.agent_hours] ?? 2;
  // Clamp to a believable range so edge combinations stay grounded.
  const exposedHoursPerWeek = Math.min(60, Math.max(1, Math.round(hoursPerDay * 6)));
  const typicalRun = describeAgents(answers.agents_used);
  const riskShort = RISK_SHORT[answers.agent_pain] ?? "no live view";
  const riskIcon = RISK_ICON[answers.agent_pain] ?? "moon";
  const keyFeature = KEY_FEATURE[answers.agent_pain] ?? KEY_FEATURE.waiting_unseen;
  const exposureCaption = `Based on ~${hoursPerDay}h of agent runtime a day, 6 days a week. Your estimate.`;

  // Their picked annoyance leads; the rest follow (no duplicates).
  const painKey = KEY_FEATURE[answers.agent_pain] ? answers.agent_pain : "waiting_unseen";
  const otherRisks = Object.entries(RISK_SHORT)
    .filter(([id]) => id !== answers.agent_pain)
    .map(([, phrase]) => phrase);
  const problemLine = `And no proper way to see or manage them: ${[riskShort, ...otherRisks.slice(0, 2)].join(", ")}.`;
  const pains = [riskShort, ...otherRisks];
  const otherFeatures = Object.entries(KEY_FEATURE)
    .filter(([id]) => id !== painKey)
    .map(([, feature]) => ({ title: feature.title, icon: feature.icon }));
  // The full product feature set (used by deep-matched concepts so "What AgentMax does"
  // always shows everything, including the one board, alongside the concept's lead feature).
  const allFeatures = Object.values(KEY_FEATURE).map((feature) => ({ title: feature.title, icon: feature.icon }));

  return {
    sessionBreak,
    macSetup,
    typicalRun,
    riskShort,
    riskIcon,
    recommendedPlan,
    planName,
    planDetail,
    hoursPerDay,
    exposedHoursPerWeek,
    exposureCaption,
    problemLine,
    pains,
    keyFeature,
    otherFeatures,
    allFeatures,
    headline:
      answers.agent_pain === "rate_limits"
        ? "AgentMax projects your limits so the cap never surprises you."
        : answers.agent_pain === "unknown_costs"
          ? "AgentMax tracks what every agent costs, right on your Mac."
          : answers.agent_pain === "waiting_unseen"
            ? "AgentMax puts every agent on one notch and jumps you to the one that needs you."
            : "AgentMax keeps your Mac awake while agents run. The display can still sleep.",
    summary: `Your biggest friction is ${sessionBreak.toLowerCase()}. For a ${macSetup.toLowerCase()} setup, ${planName} is the best fit.`,
  };
};

export const MONEY_BACK_GUARANTEE_DAYS = 15;
export const moneyBackGuaranteeLabel = `${MONEY_BACK_GUARANTEE_DAYS}-day money-back guarantee`;
export const contactFormUrl = "https://tally.so/r/RGlZzP";

export const plans: CheckoutPlan[] = [
  {
    id: "standard",
    planId: "standard",
    offer: "lifetime",
    billingMode: "one_time",
    name: "Standard",
    label: "Standard",
    originalPrice: "$29",
    price: "$19",
    detail: "1 Mac",
    description: "For one Mac running Claude Code, Codex, Cursor, and other local agents.",
    cta: "Get Standard",
    features: ["Agent Island macOS notch", "Close-lid continuity for 1 Mac", "Jump to agent", "Costs & limits"],
  },
  {
    id: "pro",
    planId: "pro",
    offer: "lifetime",
    billingMode: "one_time",
    name: "Pro",
    label: "Pro",
    originalPrice: "$39",
    price: "$29",
    detail: "Up to 5 Macs",
    description: "Best for multi-Mac developers.",
    cta: "Get Pro",
    badge: "Popular",
    badgeVariant: "primary" as const,
    features: ["Everything in Standard", "Up to 5 Macs", "Close-lid continuity", "Priority contact form"],
  },
];

export const checkoutPlans = [...plans];

export const trialCheckoutPlans: CheckoutPlan[] = [
  {
    id: "standard_trial",
    planId: "standard",
    offer: "polar_trial_7d",
    billingMode: "annual_trial",
    name: "Standard trial",
    label: "Monthly",
    price: "A$19",
    priceSuffix: "/month",
    detail: "1 Mac",
    description: "For one Mac running Claude Code, Codex, Cursor, and other local agents.",
    cta: "Try for free",
    features: ["Agent Island macOS notch", "Jump to the one that needs you", "Costs & limits", "Close-lid continuity for 1 Mac"],
  },
  {
    id: "pro_trial",
    planId: "pro",
    offer: "polar_trial_7d",
    billingMode: "annual_trial",
    name: "Pro trial",
    label: "Yearly",
    price: "A$29",
    priceSuffix: "/month",
    detail: "Up to 5 Macs",
    description: "Best for developers and consultants running agent work across more than one Mac.",
    cta: "Try for free",
    badge: "Popular",
    badgeVariant: "primary",
    features: ["Everything in Standard", "Up to 5 Macs", "Priority contact form", "Launch-price renewal"],
  },
  {
    id: "pro_lifetime",
    planId: "pro",
    offer: "site_lifetime",
    billingMode: "one_time",
    name: "Pro lifetime",
    label: "Paid",
    price: "A$39",
    detail: "Pro for life",
    description: "For early buyers who want Pro access without another renewal to track.",
    cta: "Get lifetime access",
    features: ["Everything in Pro", "Up to 5 Macs", "One payment", "Lifetime updates"],
  },
];

export const valueChips = ["Local-first", "Native macOS", "One-time payment", "Battery-aware"] as const;

// Benefit bullets in the four-beat order: see · switch · cost · care (docs/agentmax-messaging.md).
export const resultBullets = [
  "Agent Island macOS notch shows every live run at a glance",
  "Jump straight back to the agent that needs you",
  "Know what each agent costs before limits bite",
  "Close-lid continuity keeps your Mac available only while work needs it",
] as const;

// "After payment" reassurance timeline shown on checkout.
export const checkoutAfterPaymentSteps = [
  {
    title: "Download instantly after checkout",
    subtitle: "The link is also emailed to you.",
  },
  {
    title: "Install and activate with your email",
    subtitle: "Your purchase email unlocks the app.",
  },
  {
    title: "Open Agent Island macOS notch",
    subtitle: "See every agent live from your Mac's notch.",
  },
] as const;

export const paymentFaqs = [
  { question: "Is this a subscription?", answer: "No. AgentMax is a one-time purchase with lifetime updates." },
  {
    question: "What does AgentMax do?",
    answer:
      "Agent Island macOS notch shows every agent live, right where your Mac's notch already sits. Jump straight back to the one that needs you, see what each one costs, and get a heads-up before limits bite. Close-lid continuity keeps your Mac awake only while the work needs it, with battery guardrails built in.",
  },
  {
    question: "Which agents does it work with?",
    answer:
      "Claude Code, Codex CLI, Cursor, Gemini CLI, OpenCode, plus self-hosted agents like OpenClaw and Hermes. If it writes activity on your Mac, AgentMax can put it on the notch.",
  },
  {
    question: "How does it know my costs and limits?",
    answer:
      "AgentMax reads the session files your agents already write on your Mac, adds up what each one costs per provider, and tracks it against your plan's limits, with a forecast of when you'll hit the cap. Everything happens locally.",
  },
  {
    question: "What happens if I lock my Mac or close the lid?",
    answer:
      "AgentMax gives you three display modes: let the display sleep (saves battery while the run continues), sleep and lock your Mac when you step away, or keep the display on to watch it work. For lid-closed runs, enable lid-closed mode in AgentMax's settings once. It keeps your Mac awake with the lid shut so agents keep running while you relocate.",
  },
  {
    question: "Is my data sent to the cloud?",
    answer: "AgentMax is local-first. Your agents' work stays on your Mac; activation uses our license service only.",
  },
  {
    question: "Refund policy?",
    answer: "15-day money-back guarantee. Use the contact form and we will help with a full refund.",
    href: contactFormUrl,
    linkLabel: "Contact us",
  },
] as const;

export const socialProof = {
  rating: "4.9",
  ratingMax: "5",
  audience: "2,000+ users",
  avatars: [
    "/assets/reviews/man-1.webp",
    "/assets/reviews/girl-1.webp",
    "/assets/reviews/darshan-patel-headshot-96w.webp",
  ],
} as const;

export const testimonials = [
  {
    quote: "Three Claude Code agents, one Codex job, and I can finally see all of them from the menu bar. AgentMax is the missing piece.",
    author: "Theo",
    detail: "Staff engineer",
    image: "/assets/reviews/man-1.webp",
  },
  {
    quote: "It told me I'd hit my Claude cap by mid-afternoon. Rescheduled the big refactor and lost nothing.",
    author: "Lena",
    detail: "Indie hacker",
    image: "/assets/reviews/girl-1.webp",
  },
  {
    quote: "Battery threshold saved my Mac twice during long agent runs at a café, and the display sleeps while agents keep going.",
    author: "Arjun",
    detail: "DevOps consultant",
    image: "/assets/reviews/darshan-patel-headshot-96w.webp",
  },
  {
    quote: "No subscription, runs quietly in the menu bar, and I finally know what my agents cost per day. Easy buy.",
    author: "Sofia",
    detail: "Backend engineer",
    image: "/assets/reviews/girl-2.webp",
  },
  {
    quote: "Codex, Claude Code and a Gemini job at once used to mean three terminals and a prayer. Now it's one notch.",
    author: "Marcus",
    detail: "Platform engineer",
    image: "/assets/reviews/man-1.webp",
  },
  {
    quote: "Closed the lid, went home, and the overnight refactor was done in the morning. That alone was worth it.",
    author: "Priya",
    detail: "Founding engineer",
    image: "/assets/reviews/girl-1.webp",
  },
] as const;

export type PaymentExplainerStep = {
  title: string;
  description: string;
  icon: Question["options"][number]["icon"];
  tone: QuestionOptionTone;
};

// Which explainer step answers each quiz pain (index into paymentExplainerSteps).
const FEATURED_EXPLAINER_INDEX: Record<string, number> = {
  waiting_unseen: 0,
  unknown_costs: 2,
  rate_limits: 2,
  mac_sleeps: 3,
};

export const getFeaturedExplainerIndex = (answers: Answers): number =>
  FEATURED_EXPLAINER_INDEX[answers.agent_pain] ?? 0;

// "How AgentMax works": the four beats in canonical order (docs/agentmax-messaging.md).
export const paymentExplainerSteps: PaymentExplainerStep[] = [
  {
    title: "Agent Island macOS notch: see every agent, live",
    description: "Your Mac's notch goes live for Claude Code, Codex, Cursor and more. See who is working, waiting, or done.",
    icon: "desktop",
    tone: "blue",
  },
  {
    title: "Jump to agent: one click and you're there",
    description: "Click an agent and land in the terminal tab where it's working, ready to type.",
    icon: "terminal",
    tone: "violet",
  },
  {
    title: "Costs & limits: know what they cost",
    description: "Daily spend per agent, plus a heads-up before you hit your plan's limit. Read locally from the files your agents already write.",
    icon: "clock",
    tone: "amber",
  },
  {
    title: "Close-lid agent continuity: keep every run moving",
    description: "Your Mac stays awake only while agents work. The display can sleep, the lid can close, battery guardrails stay active, and continuity releases when the run is done.",
    icon: "moon",
    tone: "green",
  },
];
